<template>
  <div class="relative w-full">
    <div class="flex flex-col items-start gap-2">
      <Label v-if="label" for="searchbar" :label />
      <CommonField
        v-model="modelValue"
        class="w-full"
        id="searchbar"
        name="searchbar"
        prefix_icon="ic-search"
        :placeholder
        @focus="isInputFocused = true"
        @blur="isInputFocused = false"
        @input="$emit('input', modelValue)"
        @keydown.enter="handleSubmit"
      />
    </div>

    <Transition name="autocomplete">
      <div
        v-if="isInputFocused && autocompleteItems?.items.length"
        class="absolute z-50 mt-2 flex max-h-72 w-full flex-col gap-5 overflow-y-auto rounded-b-r2 border border-neutral-200 bg-background px-spacing_10 py-spacing_12 shadow-lg"
        @mousedown.prevent
      >
        <div class="flex w-full items-center justify-between">
          <h2
            class="font-secondary text-size_4 font-bold capitalize text-neutral-900"
          >
            {{ autocompleteItems.title }}
          </h2>

          <CommonButton
            v-if="autocompleteItems.key === 'recent'"
            label="Clear All"
            :c_style="{ type: 'ghost' }"
            @click="$emit('clearAll')"
          />
        </div>

        <div class="flex flex-col gap-spacing_4">
          <div
            v-for="item in autocompleteItems.items"
            class="flex w-full items-center justify-between rounded-md hover:bg-neutral-100"
            :key="item"
          >
            <span
              class="grow p-1 font-secondary text-size_3 font-bold text-neutral-900 hover:cursor-pointer"
              @click="handleItemClick(item)"
            >
              {{ item }}
            </span>
            <IconButton
              v-if="autocompleteItems.key === 'recent'"
              :c_style="{ type: 'ghost' }"
              @click="$emit('removeItem', item)"
            >
              <CommonUIcons
                name="ic-x"
                class="h-icon_size_2 w-icon_size_2 text-neutral-500"
              />
            </IconButton>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import type { SearchbarProps } from "~/types/schemas/common/Molecule/searchbar";
import Label from "~/components/common/Atom/Label.vue";
import CommonField from "~/components/common/Atom/Field.vue";
import CommonButton from "~/components/common/Atom/Button.vue";
import IconButton from "~/components/common/Atom/IconButton.vue";

const emit = defineEmits<{
  input: [value: string];
  clearAll: [];
  removeItem: [value: string];
  submit: [value: string];
}>();

withDefaults(defineProps<SearchbarProps>(), {
  placeholder: "Search",
});

const modelValue = defineModel<string>({ required: true });
const isInputFocused = ref(false);

const handleItemClick = (item: string) => {
  modelValue.value = item;
  isInputFocused.value = false;
  emit("submit", item);
};

const handleSubmit = () => {
  isInputFocused.value = false;
  emit("submit", modelValue.value.trim());
};
</script>

<style scoped>
.autocomplete-enter-active,
.autocomplete-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.autocomplete-enter-from,
.autocomplete-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.autocomplete-enter-to,
.autocomplete-leave-from {
  opacity: 100;
  transform: translateY(0px);
}
</style>
